import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/metronic/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/metronic/ui/Card';
import { API_BASE_URL } from '../config';
import { useAuth } from '../context/AuthContext';

const STAGES = [
  { key: 'setup', label: 'A. Setup' },
  { key: 'generate', label: 'B. Generate' },
  { key: 'curate', label: 'C. Curate' },
  { key: 'shortlist', label: 'D. Shortlist' },
  { key: 'render', label: 'E. Render' },
];

const PARTICIPANT_TIERS = ['Tier 1', 'Tier 2', 'Tier 3'];
const PARTICIPANT_TYPES = [
  'Trading House',
  'Broker / Intermediary',
  'Bank',
  'Exchange / Venue',
  'Refiner / Processor',
  'Corporate / Other',
  'Unknown',
];

const TALENT_SOURCE_PRESETS = [
  {
    key: 'tier1',
    label: 'Tier 1',
    text: 'Primary source of commercially relevant and market-active profiles',
  },
  {
    key: 'tier2',
    label: 'Tier 2',
    text: 'Secondary source with relevant execution and operations profiles; moderate mobility into target mandates',
  },
  {
    key: 'tier3',
    label: 'Tier 3',
    text: 'Strong institutional talent, usually lower mobility into pure trading mandates.',
  },
  { key: 'other', label: 'Other', text: '' },
];

function toLines(value, max = 6) {
  return String(value || '')
    .split('\n')
    .map((x) => x.trim())
    .filter(Boolean)
    .slice(0, max);
}

function participantRow(raw = {}) {
  const rowId =
    String(raw._rowId || '').trim() ||
    `participant_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  return {
    _rowId: rowId,
    tier: PARTICIPANT_TIERS.includes(raw.tier) ? raw.tier : 'Tier 3',
    company: String(raw.company || '').trim(),
    company_type: PARTICIPANT_TYPES.includes(raw.company_type) ? raw.company_type : 'Unknown',
    talent_count: Math.max(0, Number(raw.talent_count || 0)),
    talent_source_note: String(raw.talent_source_note || TALENT_SOURCE_PRESETS[0].text).trim(),
  };
}

function cleanParticipants(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.map(participantRow).filter((r) => r.company).slice(0, 40);
}

function sourcePresetKey(note) {
  const clean = String(note || '').trim();
  if (!clean) return 'tier1';
  const match = TALENT_SOURCE_PRESETS.find((x) => x.text && x.text === clean);
  return match?.key || 'other';
}

function manualCandidateRow(raw = {}) {
  return {
    _rowId: String(raw._rowId || '').trim() || `manual_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    candidate_id: String(raw.candidate_id || '').trim(),
    display_name: String(raw.display_name || raw.name || '').trim(),
    current_title: String(raw.current_title || raw.title || '').trim(),
    current_employer: String(raw.current_employer || raw.company || '').trim(),
    location: String(raw.location || '').trim(),
    priority_score_internal: Number(raw.priority_score_internal || raw.score || 0),
    commercial_strength: String(raw.commercial_strength || 'medium').toLowerCase(),
    operational_strength: String(raw.operational_strength || 'medium').toLowerCase(),
    mobility: String(raw.mobility || 'medium').toLowerCase(),
    hireability: String(raw.hireability || 'medium').toLowerCase(),
    summary_short: String(raw.summary_short || '').trim(),
  };
}

function marketStructureRow(raw = {}) {
  return {
    _rowId: String(raw._rowId || '').trim() || `ms_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    tier: String(raw.tier || 'Tier 1').trim(),
    firms: String(raw.firms || '').trim(),
    profile_type: String(raw.profile_type || '').trim(),
    notes: String(raw.notes || '').trim(),
  };
}

function priorityTargetRow(raw = {}) {
  return {
    _rowId: String(raw._rowId || '').trim() || `pt_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    candidate_id: String(raw.candidate_id || '').trim(),
    priority_label: String(raw.priority_label || raw.priority || 'MEDIUM').toUpperCase(),
    why_this_person_matters: String(raw.why_this_person_matters || raw.assessment || '').trim(),
    recommended_approach: String(raw.recommended_approach || '').trim(),
    hireability: String(raw.hireability || 'Medium').trim(),
  };
}

function strategyRow(raw = {}) {
  return {
    _rowId: String(raw._rowId || '').trim() || `st_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    candidate_type: String(raw.candidate_type || '').trim(),
    strategy: String(raw.strategy || '').trim(),
  };
}

function activationPlanRow(raw = {}) {
  return {
    _rowId: String(raw._rowId || '').trim() || `ap_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    phase: Math.max(1, Number(raw.phase || 1)),
    label: String(raw.label || '').trim(),
    duration: String(raw.duration || '').trim(),
    actions_text: Array.isArray(raw.actions) ? raw.actions.join('\n') : String(raw.actions_text || '').trim(),
  };
}

function sampleEvalRow(raw = {}) {
  return {
    _rowId: String(raw._rowId || '').trim() || `se_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    candidate_id: String(raw.candidate_id || '').trim(),
    name: String(raw.name || '').trim(),
    firm: String(raw.firm || '').trim(),
    title: String(raw.title || '').trim(),
    commercial_strength: String(raw.commercial_strength || 'medium').toLowerCase(),
    operational_strength: String(raw.operational_strength || 'medium').toLowerCase(),
    mobility: String(raw.mobility || 'medium').toLowerCase(),
    summary: String(raw.summary || raw.evaluation_summary || raw.summary_short || '').trim(),
  };
}

export default function MIEIntelligence() {
  const { currentUser } = useAuth();
  const [activeStage, setActiveStage] = useState('setup');

  const [jobs, setJobs] = useState([]);
  const [marketSpecs, setMarketSpecs] = useState([]);
  const [loadingAnchors, setLoadingAnchors] = useState(false);

  const [anchorMode, setAnchorMode] = useState('job');
  const [jobId, setJobId] = useState('');
  const [marketSpecId, setMarketSpecId] = useState('');
  const [preparedFor, setPreparedFor] = useState('');

  const [mode, setMode] = useState('intelligence');
  const [audience, setAudience] = useState('head_of_trading');
  const [tone, setTone] = useState('commercial');
  const [layout, setLayout] = useState('landscape');
  const [anonymize, setAnonymize] = useState(true);
  const [maxCandidates, setMaxCandidates] = useState(8);

  const [reportId, setReportId] = useState('');
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState('');

  const [draftLoading, setDraftLoading] = useState(false);
  const [aiDraftLoading, setAiDraftLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [renderLoading, setRenderLoading] = useState(false);

  const [summary, setSummary] = useState('');
  const [marketOverview, setMarketOverview] = useState('');
  const [keyInsightsText, setKeyInsightsText] = useState('');
  const [constraintsText, setConstraintsText] = useState('');
  const [hiringImplication, setHiringImplication] = useState('');
  const [risksText, setRisksText] = useState('');
  const [whatThisMeans, setWhatThisMeans] = useState('');
  const [ctaText, setCtaText] = useState('');
  const [activationSummary, setActivationSummary] = useState('');

  const [participants, setParticipants] = useState([]);
  const [selectedShortlistIds, setSelectedShortlistIds] = useState([]);
  const [manualCandidates, setManualCandidates] = useState([]);
  const [sectionVisibility, setSectionVisibility] = useState({
    executive_insight: true,
    talent_pool_structure: true,
    priority_targets: true,
    market_dynamics: true,
    engagement_strategy: true,
    activation_plan: true,
    sample_evaluation: true,
    summary: true,
  });
  const [evaluatedCandidates, setEvaluatedCandidates] = useState([]);
  const [selectedEvaluatedIds, setSelectedEvaluatedIds] = useState([]);
  const [evalLoading, setEvalLoading] = useState(false);

  const [marketStructureRows, setMarketStructureRows] = useState([]);
  const [priorityTargetRows, setPriorityTargetRows] = useState([]);
  const [strategyRows, setStrategyRows] = useState([]);
  const [activationPlanRows, setActivationPlanRows] = useState([]);
  const [sampleEvalRows, setSampleEvalRows] = useState([]);

  const shortlistRows = useMemo(() => {
    const list = Array.isArray(payload?.final_shortlist) ? payload.final_shortlist : [];
    if (list.length) return list;
    return Array.isArray(payload?.report_data?.candidate_pool?.candidates)
      ? payload.report_data.candidate_pool.candidates.slice(0, 80)
      : [];
  }, [payload]);

  const selectedSet = useMemo(() => new Set(selectedShortlistIds), [selectedShortlistIds]);
  const selectedEvalSet = useMemo(() => new Set(selectedEvaluatedIds), [selectedEvaluatedIds]);
  const currentJobId = useMemo(() => {
    const ctx = payload?.report_data?.job_context || {};
    return ctx?.anchor_type === 'job' ? (ctx?.job_id || '') : '';
  }, [payload]);

  const getAuthToken = useCallback(async () => {
    if (currentUser && typeof currentUser.getIdToken === 'function') return currentUser.getIdToken();
    return (
      localStorage.getItem('token') ||
      localStorage.getItem('firebaseToken') ||
      localStorage.getItem('idToken') ||
      ''
    );
  }, [currentUser]);

  const hydrateFromPayload = useCallback((nextPayload, draft = null) => {
    const c = nextPayload?.report_curation || {};
    setSummary(draft?.executive_summary_final ?? c?.executive_summary_final ?? '');
    setMarketOverview(draft?.market_overview_curated ?? c?.market_overview_curated ?? '');
    setKeyInsightsText((draft?.key_insights_curated ?? c?.key_insights_curated ?? []).join('\n'));
    setConstraintsText((draft?.market_constraints_curated ?? c?.market_constraints_curated ?? []).join('\n'));
    setHiringImplication(draft?.hiring_implication_final ?? c?.hiring_implication_final ?? '');
    setRisksText((draft?.risks_curated ?? c?.risks_curated ?? []).join('\n'));
    setWhatThisMeans(draft?.what_this_means_for_client_curated ?? c?.what_this_means_for_client_curated ?? '');
    setCtaText(draft?.cta_text_curated ?? c?.cta_text_curated ?? '');
    setActivationSummary(draft?.activation_summary_curated ?? c?.activation_summary_curated ?? '');
    setSectionVisibility({
      executive_insight: c?.section_visibility_curated?.executive_insight ?? true,
      talent_pool_structure: c?.section_visibility_curated?.talent_pool_structure ?? true,
      priority_targets: c?.section_visibility_curated?.priority_targets ?? true,
      market_dynamics: c?.section_visibility_curated?.market_dynamics ?? true,
      engagement_strategy: c?.section_visibility_curated?.engagement_strategy ?? true,
      activation_plan: c?.section_visibility_curated?.activation_plan ?? true,
      sample_evaluation: c?.section_visibility_curated?.sample_evaluation ?? true,
      summary: c?.section_visibility_curated?.summary ?? true,
    });

    const participantSeed =
      (Array.isArray(c?.market_participants_curated) && c.market_participants_curated.length
        ? c.market_participants_curated
        : nextPayload?.sections?.market_participants) || [];
    setParticipants(participantSeed.map(participantRow));
    setManualCandidates(((c?.manual_candidates_curated || []).map((x) => manualCandidateRow(x))));
    const marketStructureSeed = (Array.isArray(c?.talent_pool_structure_curated) && c.talent_pool_structure_curated.length
      ? c.talent_pool_structure_curated
      : nextPayload?.sections?.market_structure) || [];
    setMarketStructureRows(marketStructureSeed.map((x) => marketStructureRow(x)));

    const prioritySeed = (Array.isArray(c?.priority_targets_curated) && c.priority_targets_curated.length
      ? c.priority_targets_curated
      : nextPayload?.sections?.priority_cards) || [];
    setPriorityTargetRows(prioritySeed.map((x) => priorityTargetRow(x)));

    const strategySeed = (Array.isArray(c?.engagement_strategy_table_curated) && c.engagement_strategy_table_curated.length
      ? c.engagement_strategy_table_curated
      : nextPayload?.sections?.strategy_table) || [];
    setStrategyRows(strategySeed.map((x) => strategyRow(x)));

    const activationSeed = (Array.isArray(c?.activation_plan_curated) && c.activation_plan_curated.length
      ? c.activation_plan_curated
      : nextPayload?.sections?.activation_plan) || [];
    setActivationPlanRows(activationSeed.map((x) => activationPlanRow(x)));

    const sampleEvalSeed = (Array.isArray(c?.sample_candidate_evaluations_curated) && c.sample_candidate_evaluations_curated.length
      ? c.sample_candidate_evaluations_curated
      : nextPayload?.sections?.sample_eval_cards) || [];
    setSampleEvalRows(sampleEvalSeed.map((x) => sampleEvalRow(x)).slice(0, 3));
    setSelectedEvaluatedIds(
      sampleEvalSeed
        .map((x) => String(x?.candidate_id || '').trim())
        .filter(Boolean)
        .slice(0, 3)
    );

    const ids = (Array.isArray(nextPayload?.final_shortlist) ? nextPayload.final_shortlist : [])
      .map((r) => r?.candidate_id)
      .filter(Boolean);
    setSelectedShortlistIds(ids);
  }, []);

  const fetchAnchors = useCallback(async () => {
    setLoadingAnchors(true);
    setError('');
    try {
      const token = await getAuthToken();
      const [jobsRes, specsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/job_descriptions?limit=50`, {
          headers: { Authorization: token ? `Bearer ${token}` : '' },
        }),
        fetch(`${API_BASE_URL}/api/market-specs?global=1&limit=100`, {
          headers: { Authorization: token ? `Bearer ${token}` : '' },
        }),
      ]);
      const jobsData = await jobsRes.json();
      const specsData = await specsRes.json();
      const nextJobs = Array.isArray(jobsData?.job_descriptions) ? jobsData.job_descriptions : [];
      const nextSpecs = Array.isArray(specsData?.specs) ? specsData.specs : [];
      setJobs(nextJobs);
      setMarketSpecs(nextSpecs);
      if (!jobId && nextJobs.length) setJobId(nextJobs[0].id);
      if (!marketSpecId && nextSpecs.length) setMarketSpecId(nextSpecs[0].id);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoadingAnchors(false);
    }
  }, [getAuthToken, jobId, marketSpecId]);

  useEffect(() => {
    fetchAnchors();
  }, [fetchAnchors]);

  const fetchEvaluatedCandidates = useCallback(async () => {
    if (!currentJobId) {
      setEvaluatedCandidates([]);
      return;
    }
    setEvalLoading(true);
    try {
      const token = await getAuthToken();
      const res = await fetch(`${API_BASE_URL}/api/job-evaluations/${encodeURIComponent(currentJobId)}/candidates?limit=80`, {
        headers: { Authorization: token ? `Bearer ${token}` : '' },
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || `HTTP ${res.status}`);
      setEvaluatedCandidates(Array.isArray(data.candidates) ? data.candidates : []);
    } catch (e) {
      setError(e.message || String(e));
      setEvaluatedCandidates([]);
    } finally {
      setEvalLoading(false);
    }
  }, [currentJobId, getAuthToken]);

  useEffect(() => {
    if (currentJobId) fetchEvaluatedCandidates();
  }, [currentJobId, fetchEvaluatedCandidates]);

  const generateDraft = useCallback(async () => {
    const anchorId = anchorMode === 'job' ? jobId : marketSpecId;
    if (!anchorId) return;
    setDraftLoading(true);
    setError('');
    try {
      const token = await getAuthToken();
      const res = await fetch(`${API_BASE_URL}/api/mie-v2/reports/draft`, {
        method: 'POST',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          anchor: { type: anchorMode === 'job' ? 'job' : 'market_spec', id: anchorId },
          client_name: preparedFor.trim() || 'Client',
          generation_settings: { k: 140, min_score: 50, tag_mode: 'broad', scope: 'universal' },
          report_config: {
            mode,
            audience_type: audience,
            client_type: 'trading_firm',
            report_tone: tone,
            layout,
            theme: 'intelligence_briefing',
            anonymize_candidates: anonymize,
            include_salary_benchmark: false,
            include_competitor_mapping: true,
            include_risk_analysis: true,
            include_engagement_strategy: true,
            include_activation_plan: true,
            max_candidates_in_report: Number(maxCandidates || 8),
            delivery: { format: 'pdf', cta_type: 'request_profiles' },
          },
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || `HTTP ${res.status}`);
      setReportId(data.report_id || '');
      setPayload(data.payload || null);
      hydrateFromPayload(data.payload || {});
      setActiveStage('curate');
    } catch (e) {
      setError(e.message || String(e));
      setPayload(null);
    } finally {
      setDraftLoading(false);
    }
  }, [
    anchorMode,
    jobId,
    marketSpecId,
    getAuthToken,
    preparedFor,
    mode,
    audience,
    tone,
    layout,
    anonymize,
    maxCandidates,
    hydrateFromPayload,
  ]);

  const generateAiDraft = useCallback(async () => {
    if (!reportId) return;
    setAiDraftLoading(true);
    setError('');
    try {
      const token = await getAuthToken();
      const res = await fetch(`${API_BASE_URL}/api/mie-v2/reports/${encodeURIComponent(reportId)}/draft-content`, {
        method: 'POST',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || `HTTP ${res.status}`);
      setPayload(data.payload || null);
      hydrateFromPayload(data.payload || {}, data.draft || null);
      setActiveStage('curate');
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setAiDraftLoading(false);
    }
  }, [reportId, getAuthToken, hydrateFromPayload]);

  const buildCurationPayload = useCallback(() => ({
    review_status: 'in_progress',
    section_visibility_curated: sectionVisibility,
    executive_summary_final: summary,
    market_overview_curated: marketOverview,
    key_insights_curated: toLines(keyInsightsText, 5),
    market_constraints_curated: toLines(constraintsText, 6),
    hiring_implication_final: hiringImplication,
    risks_curated: toLines(risksText, 5),
    what_this_means_for_client_curated: whatThisMeans,
    cta_text_curated: ctaText,
    activation_summary_curated: activationSummary,
    market_participants_curated: cleanParticipants(participants),
    talent_pool_structure_curated: marketStructureRows
      .map((r) => marketStructureRow(r))
      .filter((r) => r.firms || r.profile_type || r.notes)
      .map(({ _rowId, ...rest }) => rest),
    priority_targets_curated: priorityTargetRows
      .map((r) => priorityTargetRow(r))
      .filter((r) => r.candidate_id || r.why_this_person_matters)
      .map(({ _rowId, ...rest }) => rest),
    engagement_strategy_table_curated: strategyRows
      .map((r) => strategyRow(r))
      .filter((r) => r.candidate_type || r.strategy)
      .map(({ _rowId, ...rest }) => rest),
    activation_plan_curated: activationPlanRows
      .map((r) => activationPlanRow(r))
      .filter((r) => r.label || r.actions_text)
      .map(({ _rowId, actions_text, ...rest }) => ({
        ...rest,
        actions: toLines(actions_text, 6),
      })),
    sample_candidate_evaluations_curated: sampleEvalRows
      .map((r) => sampleEvalRow(r))
      .slice(0, 3)
      .filter((r) => r.candidate_id || r.name || r.summary)
      .map(({ _rowId, ...rest }) => rest),
    manual_candidates_curated: manualCandidates.map((r) => {
      const row = manualCandidateRow(r);
      return {
        candidate_id: row.candidate_id || `manual_${row._rowId}`,
        display_name: row.display_name || 'Manual Candidate',
        current_title: row.current_title || 'Unknown',
        current_employer: row.current_employer || 'Unknown',
        location: row.location || 'Unknown',
        priority_score_internal: Number(row.priority_score_internal || 0),
        commercial_strength: row.commercial_strength || 'medium',
        operational_strength: row.operational_strength || 'medium',
        mobility: row.mobility || 'medium',
        hireability: row.hireability || 'medium',
        summary_short: row.summary_short || '',
      };
    }),
    candidate_overrides: shortlistRows
      .map((c) => c?.candidate_id)
      .filter(Boolean)
      .map((candidate_id) => ({
        candidate_id,
        include_in_report: selectedSet.has(candidate_id),
      })),
  }), [
    summary,
    sectionVisibility,
    marketOverview,
    keyInsightsText,
    constraintsText,
    hiringImplication,
    risksText,
    whatThisMeans,
    ctaText,
    activationSummary,
    participants,
    marketStructureRows,
    priorityTargetRows,
    strategyRows,
    activationPlanRows,
    sampleEvalRows,
    manualCandidates,
    shortlistRows,
    selectedSet,
  ]);

  const saveCuration = useCallback(async () => {
    if (!reportId) return;
    setSaveLoading(true);
    setError('');
    try {
      const token = await getAuthToken();
      const curationPayload = buildCurationPayload();
      const res = await fetch(`${API_BASE_URL}/api/mie-v2/reports/${encodeURIComponent(reportId)}/curation`, {
        method: 'PATCH',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(curationPayload),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || `HTTP ${res.status}`);
      setPayload(data.payload || null);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setSaveLoading(false);
    }
  }, [
    reportId,
    getAuthToken,
    buildCurationPayload,
  ]);

  const renderPdf = useCallback(async () => {
    if (!reportId) return;
    setRenderLoading(true);
    setError('');
    try {
      const token = await getAuthToken();
      // Always persist current local curation first so PDF reflects latest edits.
      const curationPayload = buildCurationPayload();
      const saveRes = await fetch(`${API_BASE_URL}/api/mie-v2/reports/${encodeURIComponent(reportId)}/curation`, {
        method: 'PATCH',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(curationPayload),
      });
      const saveData = await saveRes.json();
      if (!saveRes.ok || !saveData.success) throw new Error(saveData.error || `HTTP ${saveRes.status}`);
      setPayload(saveData.payload || null);

      const res = await fetch(`${API_BASE_URL}/api/mie-v2/reports/${encodeURIComponent(reportId)}/render`, {
        method: 'POST',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ format: 'pdf' }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || `HTTP ${res.status}`);
      if (!data.pdfB64) throw new Error('No PDF payload returned');
      const bytes = Uint8Array.from(atob(data.pdfB64), (c) => c.charCodeAt(0));
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mie_v2_${reportId || 'report'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setRenderLoading(false);
    }
  }, [
    reportId,
    getAuthToken,
    buildCurationPayload,
  ]);

  const progressIndex = STAGES.findIndex((s) => s.key === activeStage);

  return (
    <div className="space-y-4 pb-24">
      <Card>
        <CardHeader>
          <CardTitle>ALICE Market Intelligence Engine (MIE) v2</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-gray-600 dark:text-gray-300">
            Structured workflow for Setup → Generate → Curate → Shortlist → Render.
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {STAGES.map((stage, idx) => (
              <button
                key={stage.key}
                type="button"
                onClick={() => setActiveStage(stage.key)}
                className={`rounded-md px-3 py-1.5 text-xs font-medium border ${
                  activeStage === stage.key
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : idx <= progressIndex
                      ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                      : 'bg-white text-gray-600 border-gray-200'
                }`}
              >
                {stage.label}
              </button>
            ))}
          </div>
          {reportId && (
            <div className="mt-2 text-xs text-indigo-700 dark:text-indigo-300">
              Report ID: <strong>{reportId}</strong>
            </div>
          )}
        </CardContent>
      </Card>

      {activeStage === 'setup' && (
        <Card>
          <CardHeader><CardTitle>Stage A · Setup</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs mb-1">Anchor</label>
                <select className="w-full rounded-md border px-3 py-2 text-sm" value={anchorMode} onChange={(e) => setAnchorMode(e.target.value)}>
                  <option value="job">Job</option>
                  <option value="market">Market Spec</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs mb-1">{anchorMode === 'job' ? 'Select Job' : 'Select Market Spec'}</label>
                <select
                  className="w-full rounded-md border px-3 py-2 text-sm"
                  value={anchorMode === 'job' ? jobId : marketSpecId}
                  onChange={(e) => (anchorMode === 'job' ? setJobId(e.target.value) : setMarketSpecId(e.target.value))}
                  disabled={loadingAnchors}
                >
                  {(anchorMode === 'job' ? jobs : marketSpecs).map((x) => (
                    <option key={x.id} value={x.id}>{x.title || x.name || x.segment || x.id}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs mb-1">Prepared For (company)</label>
                <input className="w-full rounded-md border px-3 py-2 text-sm" value={preparedFor} onChange={(e) => setPreparedFor(e.target.value)} placeholder="e.g. CMC Markets" />
              </div>

              <div>
                <label className="block text-xs mb-1">Mode</label>
                <select className="w-full rounded-md border px-3 py-2 text-sm" value={mode} onChange={(e) => setMode(e.target.value)}>
                  <option value="lite">Lite</option>
                  <option value="intelligence">Intelligence</option>
                  <option value="execution">Execution</option>
                </select>
              </div>
              <div>
                <label className="block text-xs mb-1">Audience</label>
                <select className="w-full rounded-md border px-3 py-2 text-sm" value={audience} onChange={(e) => setAudience(e.target.value)}>
                  <option value="head_of_trading">Head of Trading</option>
                  <option value="hr_leader">HR Leader</option>
                  <option value="ta_leader">TA Leader</option>
                  <option value="ceo">CEO</option>
                </select>
              </div>
              <div>
                <label className="block text-xs mb-1">Tone</label>
                <select className="w-full rounded-md border px-3 py-2 text-sm" value={tone} onChange={(e) => setTone(e.target.value)}>
                  <option value="commercial">Commercial</option>
                  <option value="analytical">Analytical</option>
                  <option value="executive">Executive</option>
                  <option value="consultative">Consultative</option>
                </select>
              </div>
              <div>
                <label className="block text-xs mb-1">Layout</label>
                <select className="w-full rounded-md border px-3 py-2 text-sm" value={layout} onChange={(e) => setLayout(e.target.value)}>
                  <option value="landscape">Landscape</option>
                  <option value="portrait">Portrait</option>
                </select>
              </div>
              <div>
                <label className="block text-xs mb-1">Max Candidates</label>
                <input type="number" min={1} max={25} className="w-full rounded-md border px-3 py-2 text-sm" value={maxCandidates} onChange={(e) => setMaxCandidates(Number(e.target.value || 8))} />
              </div>
              <div className="flex items-center gap-2 md:col-span-2">
                <input id="anonymize" type="checkbox" checked={anonymize} onChange={(e) => setAnonymize(e.target.checked)} />
                <label htmlFor="anonymize" className="text-sm">Anonymize candidates</label>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {activeStage === 'generate' && (
        <Card>
          <CardHeader><CardTitle>Stage B · Generate</CardTitle></CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              <Button onClick={generateDraft} disabled={draftLoading || !(anchorMode === 'job' ? jobId : marketSpecId)}>
                {draftLoading ? 'Generating draft...' : '1) Generate Draft'}
              </Button>
              <Button
                className="bg-violet-600 hover:bg-violet-700 text-white"
                onClick={generateAiDraft}
                disabled={aiDraftLoading || !reportId}
              >
                {aiDraftLoading ? 'Drafting content...' : '1b) Generate AI Content Draft'}
              </Button>
            </div>
            <div className="mt-3 text-sm text-gray-600">
              {payload
                ? `Draft ready. Pool: ${payload?.report_data?.candidate_pool?.total_candidates_found ?? 0}, shortlist: ${payload?.final_shortlist?.length ?? 0}.`
                : 'Generate a draft to populate the curation workspace.'}
            </div>
          </CardContent>
        </Card>
      )}

      {activeStage === 'curate' && (
        <Card>
          <CardHeader><CardTitle>Stage C · Curate Content</CardTitle></CardHeader>
          <CardContent>
            <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              Priority Targets are evaluation-gated for this job. Auto-population uses candidates evaluated against this exact job.
            </div>
            <div className="mb-3 rounded-md border border-indigo-200 bg-indigo-50 p-3">
              <div className="text-xs font-semibold mb-2">Section visibility (toggle to remove section from rendered PDF)</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                {[
                  ['executive_insight', 'Executive Insight'],
                  ['talent_pool_structure', 'Talent Pool Structure'],
                  ['priority_targets', 'Priority Targets'],
                  ['market_dynamics', 'Market Dynamics'],
                  ['engagement_strategy', 'Engagement Strategy'],
                  ['activation_plan', 'Activation Plan'],
                  ['sample_evaluation', 'Sample Evaluation'],
                  ['summary', 'Summary & CTA'],
                ].map(([key, label]) => (
                  <label key={key} className="inline-flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={Boolean(sectionVisibility[key])}
                      onChange={(e) => setSectionVisibility((prev) => ({ ...prev, [key]: e.target.checked }))}
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2 rounded-md border border-sky-200 bg-sky-50 p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-semibold">Evaluated Candidates (for Sample Evaluation / Priority context)</div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={fetchEvaluatedCandidates} disabled={!currentJobId || evalLoading}>
                      {evalLoading ? 'Loading...' : 'Refresh evaluated candidates'}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={selectedEvalSet.size === 0}
                      onClick={() => {
                        const rows = evaluatedCandidates
                          .filter((c) => selectedEvalSet.has(String(c?.candidate_id || '')))
                          .slice(0, 3)
                          .map((c) => sampleEvalRow({
                            candidate_id: c.candidate_id,
                            name: c.initials || c.name,
                            firm: c.company || 'Unknown',
                            title: c.title || 'Unknown',
                            commercial_strength: c.score >= 75 ? 'high' : (c.score >= 60 ? 'medium' : 'low'),
                            operational_strength: c.score >= 70 ? 'high' : (c.score >= 55 ? 'medium' : 'low'),
                            mobility: c.score >= 70 ? 'high' : (c.score >= 55 ? 'medium' : 'low'),
                            summary: c.summary || '',
                          }));
                        setSampleEvalRows(rows);
                      }}
                    >
                      Use selected in sample cards
                    </Button>
                  </div>
                </div>
                {!currentJobId && <div className="text-xs text-gray-500">Evaluated candidates are available for job-anchored reports only.</div>}
                {currentJobId && (
                  <div className="max-h-44 overflow-auto rounded border">
                    <table className="min-w-full text-xs">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-2 py-2 text-left">Use</th>
                          <th className="px-2 py-2 text-left">Candidate</th>
                          <th className="px-2 py-2 text-left">Title</th>
                          <th className="px-2 py-2 text-left">Score</th>
                        </tr>
                      </thead>
                      <tbody>
                        {evaluatedCandidates.length === 0 && (
                          <tr><td className="px-2 py-2 text-gray-500" colSpan={4}>No evaluated candidates found for this job.</td></tr>
                        )}
                        {evaluatedCandidates.map((c) => {
                          const cid = String(c?.candidate_id || '').trim();
                          return (
                            <tr key={cid || Math.random()} className="border-t">
                              <td className="px-2 py-2">
                                <input
                                  type="checkbox"
                                  checked={selectedEvalSet.has(cid)}
                                  onChange={(e) => {
                                    setSelectedEvaluatedIds((prev) => {
                                      const next = e.target.checked ? [...new Set([...prev, cid])] : prev.filter((x) => x !== cid);
                                      return next.slice(0, 3);
                                    });
                                  }}
                                />
                              </td>
                              <td className="px-2 py-2">{c?.initials || c?.name || 'Unknown'}</td>
                              <td className="px-2 py-2">{c?.title || '—'}</td>
                              <td className="px-2 py-2">{c?.score ?? '—'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="md:col-span-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-900 mt-1">Executive Section</div>
              <div className="md:col-span-2 rounded-md border border-blue-100 bg-blue-50/40 p-2">
                <label className="block text-xs mb-1">Executive summary</label>
                <textarea rows={3} className="w-full rounded-md border px-3 py-2 text-sm" value={summary} onChange={(e) => setSummary(e.target.value)} />
              </div>
              <div className="md:col-span-2 rounded-md border border-blue-100 bg-blue-50/40 p-2">
                <label className="block text-xs mb-1">Market overview narrative</label>
                <textarea rows={3} className="w-full rounded-md border px-3 py-2 text-sm" value={marketOverview} onChange={(e) => setMarketOverview(e.target.value)} />
              </div>
              <div className="rounded-md border border-blue-100 bg-blue-50/40 p-2">
                <label className="block text-xs mb-1">Key insights (one per line)</label>
                <textarea rows={4} className="w-full rounded-md border px-3 py-2 text-sm" value={keyInsightsText} onChange={(e) => setKeyInsightsText(e.target.value)} />
              </div>
              <div className="rounded-md border border-blue-100 bg-blue-50/40 p-2">
                <label className="block text-xs mb-1">Market constraints (one per line)</label>
                <textarea rows={4} className="w-full rounded-md border px-3 py-2 text-sm" value={constraintsText} onChange={(e) => setConstraintsText(e.target.value)} />
              </div>
              <div className="rounded-md border border-blue-100 bg-blue-50/40 p-2">
                <label className="block text-xs mb-1">Hiring implication</label>
                <textarea rows={3} className="w-full rounded-md border px-3 py-2 text-sm" value={hiringImplication} onChange={(e) => setHiringImplication(e.target.value)} />
              </div>
              <div className="rounded-md border border-blue-100 bg-blue-50/40 p-2">
                <label className="block text-xs mb-1">Risk factors (one per line)</label>
                <textarea rows={3} className="w-full rounded-md border px-3 py-2 text-sm" value={risksText} onChange={(e) => setRisksText(e.target.value)} />
              </div>
              <div className="md:col-span-2 rounded-md border border-slate-200 bg-slate-50 p-2">
                <label className="block text-xs mb-1">What this means for client</label>
                <textarea rows={3} className="w-full rounded-md border px-3 py-2 text-sm" value={whatThisMeans} onChange={(e) => setWhatThisMeans(e.target.value)} />
              </div>
              <div className="md:col-span-2 rounded-md border border-slate-200 bg-slate-50 p-2">
                <label className="block text-xs mb-1">Summary CTA</label>
                <textarea rows={2} className="w-full rounded-md border px-3 py-2 text-sm" value={ctaText} onChange={(e) => setCtaText(e.target.value)} />
              </div>
              <div className="md:col-span-2 rounded-md border border-violet-200 bg-violet-50 p-2">
                <label className="block text-xs mb-1">Activation plan summary</label>
                <textarea rows={2} className="w-full rounded-md border px-3 py-2 text-sm" value={activationSummary} onChange={(e) => setActivationSummary(e.target.value)} />
              </div>
              <div className="md:col-span-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-900 mt-1">Structure, Targets & Strategy</div>
              <div className="md:col-span-2 rounded-md border border-amber-200 bg-amber-50/30 p-2">
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs">Talent Pool Structure</label>
                  <Button size="sm" variant="outline" onClick={() => setMarketStructureRows((prev) => [...prev, marketStructureRow({})])}>Add Row</Button>
                </div>
                <div className="overflow-x-auto rounded-md border">
                  <table className="min-w-full text-xs">
                    <thead className="bg-gray-50"><tr><th className="px-2 py-2 text-left">Tier</th><th className="px-2 py-2 text-left">Firms</th><th className="px-2 py-2 text-left">Profile Type</th><th className="px-2 py-2 text-left">Notes</th><th className="px-2 py-2 text-left">Action</th></tr></thead>
                    <tbody>
                      {marketStructureRows.length === 0 && <tr><td className="px-2 py-2 text-gray-500" colSpan={5}>No rows yet.</td></tr>}
                      {marketStructureRows.map((row, idx) => (
                        <tr key={row._rowId} className="border-t">
                          <td className="px-2 py-2"><select className="rounded border px-2 py-1" value={row.tier} onChange={(e) => setMarketStructureRows((prev) => prev.map((p, i) => (i === idx ? { ...p, tier: e.target.value } : p)))}><option>Tier 1</option><option>Tier 2</option><option>Tier 3</option></select></td>
                          <td className="px-2 py-2"><input className="rounded border px-2 py-1 w-56" value={row.firms} onChange={(e) => setMarketStructureRows((prev) => prev.map((p, i) => (i === idx ? { ...p, firms: e.target.value } : p)))} /></td>
                          <td className="px-2 py-2"><input className="rounded border px-2 py-1 w-44" value={row.profile_type} onChange={(e) => setMarketStructureRows((prev) => prev.map((p, i) => (i === idx ? { ...p, profile_type: e.target.value } : p)))} /></td>
                          <td className="px-2 py-2"><input className="rounded border px-2 py-1 w-72" value={row.notes} onChange={(e) => setMarketStructureRows((prev) => prev.map((p, i) => (i === idx ? { ...p, notes: e.target.value } : p)))} /></td>
                          <td className="px-2 py-2"><Button size="sm" variant="destructive" onClick={() => setMarketStructureRows((prev) => prev.filter((_, i) => i !== idx))}>Remove</Button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="md:col-span-2 rounded-md border border-orange-200 bg-orange-50/30 p-2">
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs">Priority Targets</label>
                  <Button size="sm" variant="outline" onClick={() => setPriorityTargetRows((prev) => [...prev, priorityTargetRow({})])}>Add Target</Button>
                </div>
                <div className="space-y-2">
                  {priorityTargetRows.length === 0 && <div className="text-xs text-gray-500">No priority targets yet.</div>}
                  {priorityTargetRows.map((row, idx) => (
                    <div key={row._rowId} className="rounded-md border p-2">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                        <input className="rounded border px-2 py-1 text-xs" placeholder="Candidate ID" value={row.candidate_id} onChange={(e) => setPriorityTargetRows((prev) => prev.map((p, i) => (i === idx ? { ...p, candidate_id: e.target.value } : p)))} />
                        <select className="rounded border px-2 py-1 text-xs" value={row.priority_label} onChange={(e) => setPriorityTargetRows((prev) => prev.map((p, i) => (i === idx ? { ...p, priority_label: e.target.value } : p)))}><option>HIGH</option><option>MEDIUM</option><option>LOW</option></select>
                        <input className="rounded border px-2 py-1 text-xs" placeholder="Hireability" value={row.hireability} onChange={(e) => setPriorityTargetRows((prev) => prev.map((p, i) => (i === idx ? { ...p, hireability: e.target.value } : p)))} />
                      </div>
                      <textarea rows={2} className="mt-2 w-full rounded border px-2 py-1 text-xs" placeholder="Why this person matters" value={row.why_this_person_matters} onChange={(e) => setPriorityTargetRows((prev) => prev.map((p, i) => (i === idx ? { ...p, why_this_person_matters: e.target.value } : p)))} />
                      <textarea rows={2} className="mt-2 w-full rounded border px-2 py-1 text-xs" placeholder="Recommended approach" value={row.recommended_approach} onChange={(e) => setPriorityTargetRows((prev) => prev.map((p, i) => (i === idx ? { ...p, recommended_approach: e.target.value } : p)))} />
                      <div className="mt-2"><Button size="sm" variant="destructive" onClick={() => setPriorityTargetRows((prev) => prev.filter((_, i) => i !== idx))}>Remove</Button></div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="rounded-md border border-emerald-200 bg-emerald-50/30 p-2">
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs">Engagement Strategy Table</label>
                  <Button size="sm" variant="outline" onClick={() => setStrategyRows((prev) => [...prev, strategyRow({})])}>Add</Button>
                </div>
                <div className="space-y-2">
                  {strategyRows.map((row, idx) => (
                    <div key={row._rowId} className="rounded-md border p-2 space-y-2">
                      <input className="w-full rounded border px-2 py-1 text-xs" placeholder="Candidate type / segment" value={row.candidate_type} onChange={(e) => setStrategyRows((prev) => prev.map((p, i) => (i === idx ? { ...p, candidate_type: e.target.value } : p)))} />
                      <textarea rows={2} className="w-full rounded border px-2 py-1 text-xs" placeholder="Strategy" value={row.strategy} onChange={(e) => setStrategyRows((prev) => prev.map((p, i) => (i === idx ? { ...p, strategy: e.target.value } : p)))} />
                      <Button size="sm" variant="destructive" onClick={() => setStrategyRows((prev) => prev.filter((_, i) => i !== idx))}>Remove</Button>
                    </div>
                  ))}
                </div>
              </div>
              <div className="rounded-md border border-violet-200 bg-violet-50/30 p-2">
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs">Activation Plan</label>
                  <Button size="sm" variant="outline" onClick={() => setActivationPlanRows((prev) => [...prev, activationPlanRow({ phase: prev.length + 1 })])}>Add</Button>
                </div>
                <div className="space-y-2">
                  {activationPlanRows.map((row, idx) => (
                    <div key={row._rowId} className="rounded-md border p-2">
                      <div className="grid grid-cols-3 gap-2">
                        <input type="number" min={1} className="rounded border px-2 py-1 text-xs" placeholder="Phase" value={row.phase} onChange={(e) => setActivationPlanRows((prev) => prev.map((p, i) => (i === idx ? { ...p, phase: Number(e.target.value || 1) } : p)))} />
                        <input className="rounded border px-2 py-1 text-xs" placeholder="Label" value={row.label} onChange={(e) => setActivationPlanRows((prev) => prev.map((p, i) => (i === idx ? { ...p, label: e.target.value } : p)))} />
                        <input className="rounded border px-2 py-1 text-xs" placeholder="Duration (e.g. Week 1)" value={row.duration} onChange={(e) => setActivationPlanRows((prev) => prev.map((p, i) => (i === idx ? { ...p, duration: e.target.value } : p)))} />
                      </div>
                      <textarea rows={3} className="mt-2 w-full rounded border px-2 py-1 text-xs" placeholder="Actions (one per line)" value={row.actions_text} onChange={(e) => setActivationPlanRows((prev) => prev.map((p, i) => (i === idx ? { ...p, actions_text: e.target.value } : p)))} />
                      <div className="mt-2"><Button size="sm" variant="destructive" onClick={() => setActivationPlanRows((prev) => prev.filter((_, i) => i !== idx))}>Remove</Button></div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="md:col-span-2 rounded-md border border-rose-200 bg-rose-50/30 p-2">
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs">Sample Evaluation Cards (max 3)</label>
                  <Button size="sm" variant="outline" disabled={sampleEvalRows.length >= 3} onClick={() => setSampleEvalRows((prev) => [...prev, sampleEvalRow({})].slice(0, 3))}>Add Card</Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  {sampleEvalRows.map((row, idx) => (
                    <div key={row._rowId} className="rounded-md border p-2">
                      <input className="w-full rounded border px-2 py-1 text-xs mb-1" placeholder="Candidate ID" value={row.candidate_id} onChange={(e) => setSampleEvalRows((prev) => prev.map((p, i) => (i === idx ? { ...p, candidate_id: e.target.value } : p)))} />
                      <input className="w-full rounded border px-2 py-1 text-xs mb-1" placeholder="Display Name" value={row.name} onChange={(e) => setSampleEvalRows((prev) => prev.map((p, i) => (i === idx ? { ...p, name: e.target.value } : p)))} />
                      <input className="w-full rounded border px-2 py-1 text-xs mb-1" placeholder="Firm" value={row.firm} onChange={(e) => setSampleEvalRows((prev) => prev.map((p, i) => (i === idx ? { ...p, firm: e.target.value } : p)))} />
                      <input className="w-full rounded border px-2 py-1 text-xs mb-1" placeholder="Title" value={row.title} onChange={(e) => setSampleEvalRows((prev) => prev.map((p, i) => (i === idx ? { ...p, title: e.target.value } : p)))} />
                      <div className="grid grid-cols-3 gap-1 mb-1">
                        <select className="rounded border px-2 py-1 text-xs" value={row.commercial_strength} onChange={(e) => setSampleEvalRows((prev) => prev.map((p, i) => (i === idx ? { ...p, commercial_strength: e.target.value } : p)))}><option value="low">low</option><option value="medium">medium</option><option value="high">high</option></select>
                        <select className="rounded border px-2 py-1 text-xs" value={row.operational_strength} onChange={(e) => setSampleEvalRows((prev) => prev.map((p, i) => (i === idx ? { ...p, operational_strength: e.target.value } : p)))}><option value="low">low</option><option value="medium">medium</option><option value="high">high</option></select>
                        <select className="rounded border px-2 py-1 text-xs" value={row.mobility} onChange={(e) => setSampleEvalRows((prev) => prev.map((p, i) => (i === idx ? { ...p, mobility: e.target.value } : p)))}><option value="low">low</option><option value="medium">medium</option><option value="high">high</option></select>
                      </div>
                      <textarea rows={3} className="w-full rounded border px-2 py-1 text-xs mb-1" placeholder="Evaluation summary" value={row.summary} onChange={(e) => setSampleEvalRows((prev) => prev.map((p, i) => (i === idx ? { ...p, summary: e.target.value } : p)))} />
                      <Button size="sm" variant="destructive" onClick={() => setSampleEvalRows((prev) => prev.filter((_, i) => i !== idx))}>Remove</Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {activeStage === 'shortlist' && (
        <Card>
          <CardHeader><CardTitle>Stage D · Final Shortlist</CardTitle></CardHeader>
          <CardContent>
            <div className="text-xs text-gray-600 mb-2">Tick candidates to include in report output.</div>
            <div className="flex gap-2 mb-3">
              <Button
                size="sm"
                className="bg-orange-500 hover:bg-orange-600 text-white"
                onClick={() => setManualCandidates((prev) => [...prev, manualCandidateRow({})])}
              >
                Add Candidate
              </Button>
              <Button size="sm" variant="outline" onClick={() => setManualCandidates([])}>
                Clear Manual Candidates
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left">Use</th>
                    <th className="px-3 py-2 text-left">Name</th>
                    <th className="px-3 py-2 text-left">Title</th>
                    <th className="px-3 py-2 text-left">Company</th>
                    <th className="px-3 py-2 text-left">Score</th>
                  </tr>
                </thead>
                <tbody>
                  {shortlistRows.map((row) => {
                    const id = row?.candidate_id;
                    if (!id) return null;
                    return (
                      <tr key={id} className="border-t">
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            checked={selectedSet.has(id)}
                            onChange={(e) =>
                              setSelectedShortlistIds((prev) => (e.target.checked ? [...new Set([...prev, id])] : prev.filter((x) => x !== id)))
                            }
                          />
                        </td>
                        <td className="px-3 py-2">{row.display_name || row.name || 'Unknown'}</td>
                        <td className="px-3 py-2">{row.current_title || row.title || '—'}</td>
                        <td className="px-3 py-2">{row.current_employer || row.company || '—'}</td>
                        <td className="px-3 py-2">{row.priority_score_internal || row.score || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {manualCandidates.length > 0 && (
              <div className="mt-4 overflow-x-auto">
                <div className="text-xs text-gray-600 mb-2">Manual candidates (always included when saved/rendered)</div>
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left">Name</th>
                      <th className="px-3 py-2 text-left">Title</th>
                      <th className="px-3 py-2 text-left">Company</th>
                      <th className="px-3 py-2 text-left">Location</th>
                      <th className="px-3 py-2 text-left">Score</th>
                      <th className="px-3 py-2 text-left">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {manualCandidates.map((row, idx) => (
                      <tr key={row._rowId || `manual-${idx}`} className="border-t">
                        <td className="px-3 py-2"><input className="rounded-md border px-2 py-1 w-44" value={row.display_name} onChange={(e) => setManualCandidates((prev) => prev.map((p, i) => (i === idx ? { ...p, display_name: e.target.value } : p)))} /></td>
                        <td className="px-3 py-2"><input className="rounded-md border px-2 py-1 w-56" value={row.current_title} onChange={(e) => setManualCandidates((prev) => prev.map((p, i) => (i === idx ? { ...p, current_title: e.target.value } : p)))} /></td>
                        <td className="px-3 py-2"><input className="rounded-md border px-2 py-1 w-52" value={row.current_employer} onChange={(e) => setManualCandidates((prev) => prev.map((p, i) => (i === idx ? { ...p, current_employer: e.target.value } : p)))} /></td>
                        <td className="px-3 py-2"><input className="rounded-md border px-2 py-1 w-28" value={row.location} onChange={(e) => setManualCandidates((prev) => prev.map((p, i) => (i === idx ? { ...p, location: e.target.value } : p)))} /></td>
                        <td className="px-3 py-2"><input type="number" min={0} className="rounded-md border px-2 py-1 w-20" value={row.priority_score_internal} onChange={(e) => setManualCandidates((prev) => prev.map((p, i) => (i === idx ? { ...p, priority_score_internal: Number(e.target.value || 0) } : p)))} /></td>
                        <td className="px-3 py-2"><Button size="sm" variant="destructive" onClick={() => setManualCandidates((prev) => prev.filter((_, i) => i !== idx))}>Remove</Button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {activeStage === 'render' && (
        <Card>
          <CardHeader><CardTitle>Stage E · Render</CardTitle></CardHeader>
          <CardContent>
            <div className="text-sm text-gray-700">
              Save curation, then render PDF. Current selected shortlist: <strong>{selectedShortlistIds.length}</strong>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle>Market Participants (Tier 1 / 2 / 3)</CardTitle></CardHeader>
        <CardContent>
          <div className="flex gap-2 mb-2">
            <Button
              size="sm"
              className="bg-orange-500 hover:bg-orange-600 text-white"
              onClick={() =>
                setParticipants((prev) => [
                  ...prev,
                  participantRow({ tier: 'Tier 3', company: '', company_type: 'Unknown', talent_count: 0, talent_source_note: TALENT_SOURCE_PRESETS[0].text }),
                ])
              }
            >
              Add Company
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                const seed = payload?.sections?.market_participants || [];
                setParticipants(seed.map(participantRow));
              }}
            >
              Reset From Auto-List
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left">Tier</th>
                  <th className="px-3 py-2 text-left">Company</th>
                  <th className="px-3 py-2 text-left">Type</th>
                  <th className="px-3 py-2 text-left">Talent Count</th>
                  <th className="px-3 py-2 text-left">Likely Talent Source</th>
                  <th className="px-3 py-2 text-left">Action</th>
                </tr>
              </thead>
              <tbody>
                {participants.length === 0 && (
                  <tr><td className="px-3 py-3 text-gray-500" colSpan={6}>No rows yet. Click Add Company.</td></tr>
                )}
                {participants.map((row, idx) => (
                  <tr key={row._rowId || `row-${idx}`} className="border-t">
                    <td className="px-3 py-2">
                      <select
                        className="rounded-md border px-2 py-1"
                        value={row.tier}
                        onChange={(e) => setParticipants((prev) => prev.map((p, i) => (i === idx ? { ...p, tier: e.target.value } : p)))}
                      >
                        {PARTICIPANT_TIERS.map((tier) => <option key={tier} value={tier}>{tier}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      <input className="rounded-md border px-2 py-1 w-56" value={row.company} onChange={(e) => setParticipants((prev) => prev.map((p, i) => (i === idx ? { ...p, company: e.target.value } : p)))} />
                    </td>
                    <td className="px-3 py-2">
                      <select
                        className="rounded-md border px-2 py-1"
                        value={row.company_type}
                        onChange={(e) => setParticipants((prev) => prev.map((p, i) => (i === idx ? { ...p, company_type: e.target.value } : p)))}
                      >
                        {PARTICIPANT_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      <input type="number" min={0} className="rounded-md border px-2 py-1 w-20" value={row.talent_count} onChange={(e) => setParticipants((prev) => prev.map((p, i) => (i === idx ? { ...p, talent_count: Math.max(0, Number(e.target.value || 0)) } : p)))} />
                    </td>
                    <td className="px-3 py-2">
                      <div className="space-y-1">
                        <select
                          className="rounded-md border px-2 py-1 w-80"
                          value={sourcePresetKey(row.talent_source_note)}
                          onChange={(e) => {
                            const preset = TALENT_SOURCE_PRESETS.find((x) => x.key === e.target.value);
                            setParticipants((prev) => prev.map((p, i) => (
                              i === idx
                                ? { ...p, talent_source_note: preset?.key === 'other' ? p.talent_source_note : (preset?.text || '') }
                                : p
                            )));
                          }}
                        >
                          {TALENT_SOURCE_PRESETS.map((preset) => (
                            <option key={preset.key} value={preset.key}>{preset.label}</option>
                          ))}
                        </select>
                        {sourcePresetKey(row.talent_source_note) === 'other' && (
                          <input
                            className="rounded-md border px-2 py-1 w-80"
                            placeholder="Enter custom likely talent source"
                            value={row.talent_source_note}
                            onChange={(e) => setParticipants((prev) => prev.map((p, i) => (i === idx ? { ...p, talent_source_note: e.target.value } : p)))}
                          />
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <Button size="sm" variant="destructive" onClick={() => setParticipants((prev) => prev.filter((_, i) => i !== idx))}>Remove</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {error && <div className="text-sm text-red-600">{error}</div>}

      <div className="fixed bottom-4 right-6 z-40">
        <div className="rounded-xl border bg-white shadow-lg px-3 py-2 flex items-center gap-2">
          <Button variant="outline" onClick={saveCuration} disabled={saveLoading || !reportId}>
            {saveLoading ? 'Saving...' : 'Save Curation'}
          </Button>
          <Button onClick={renderPdf} disabled={renderLoading || !reportId}>
            {renderLoading ? 'Rendering...' : 'Render PDF'}
          </Button>
        </div>
      </div>

      <div className="text-xs text-gray-500">
        Need legacy full graph + talent-pool controls? Open{' '}
        <Link className="text-indigo-600 underline" to="/talent-insights">
          Talent Insights (legacy)
        </Link>.
      </div>
    </div>
  );
}
